import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGamesBase } from '../../utils/gamesBase';
import { useFitScale } from '../../components/Games2/FitToWidth';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';

// Triangle properties
const ORIENTATIONS = ['up', 'right', 'down', 'left'];
const COLORS = ['#fe0000', '#ffff00', '#00ffff', '#00ff01']; // Red, Yellow, Blue (cyan), Green
const COLOR_NAMES = ['RED', 'YELLOW', 'BLUE', 'GREEN'];
const BORDER_COLOR = '#021717';
const DOT_COUNTS = [0, 1, 2, 3, 4];
const RULES = ['ORIENTATION', 'COLOUR', 'NUMBER OF POINTS'];

// Generate a random triangle
const generateTriangle = () => ({
  orientation: ORIENTATIONS[Math.floor(Math.random() * ORIENTATIONS.length)],
  color: Math.floor(Math.random() * COLORS.length),
  dots: DOT_COUNTS[Math.floor(Math.random() * DOT_COUNTS.length)],
});

// Generate a triangle that matches the previous one on at most ONE rule (0 or 1 match, never 2+)
const generateValidTriangle = (previousTriangle) => {
  if (!previousTriangle) {
    return generateTriangle();
  }
  
  let attempts = 0;
  const maxAttempts = 100;
  
  while (attempts < maxAttempts) {
    const newTriangle = generateTriangle();
    
    // Count how many rules match
    let matchCount = 0;
    if (newTriangle.orientation === previousTriangle.orientation) matchCount++;
    if (newTriangle.color === previousTriangle.color) matchCount++;
    if (newTriangle.dots === previousTriangle.dots) matchCount++;
    
    // Accept only if 0 or 1 rules match
    if (matchCount <= 1) {
      return newTriangle;
    }
    
    attempts++;
  }
  
  // Fallback: force exactly one property to be different
  const newTriangle = { ...previousTriangle };
  const propertyToChange = Math.floor(Math.random() * 3);
  
  if (propertyToChange === 0) {
    // Change orientation
    const otherOrientations = ORIENTATIONS.filter(o => o !== previousTriangle.orientation);
    newTriangle.orientation = otherOrientations[Math.floor(Math.random() * otherOrientations.length)];
    // Also change one more property to ensure max 1 match
    const otherColors = [0, 1, 2, 3].filter(c => c !== previousTriangle.color);
    newTriangle.color = otherColors[Math.floor(Math.random() * otherColors.length)];
  } else if (propertyToChange === 1) {
    // Change color
    const otherColors = [0, 1, 2, 3].filter(c => c !== previousTriangle.color);
    newTriangle.color = otherColors[Math.floor(Math.random() * otherColors.length)];
    // Also change dots
    const otherDots = DOT_COUNTS.filter(d => d !== previousTriangle.dots);
    newTriangle.dots = otherDots[Math.floor(Math.random() * otherDots.length)];
  } else {
    // Change dots
    const otherDots = DOT_COUNTS.filter(d => d !== previousTriangle.dots);
    newTriangle.dots = otherDots[Math.floor(Math.random() * otherDots.length)];
    // Also change orientation
    const otherOrientations = ORIENTATIONS.filter(o => o !== previousTriangle.orientation);
    newTriangle.orientation = otherOrientations[Math.floor(Math.random() * otherOrientations.length)];
  }
  
  return newTriangle;
};

// Get two different random rules
const getRandomRules = () => {
  const shuffled = [...RULES].sort(() => Math.random() - 0.5);
  return { topRule: shuffled[0], bottomRule: shuffled[1] };
};

// Check if two triangles match on a specific rule
const checkRuleMatch = (prev, current, rule) => {
  switch (rule) {
    case 'ORIENTATION':
      return prev.orientation === current.orientation;
    case 'COLOUR':
      return prev.color === current.color;
    case 'NUMBER OF POINTS':
      return prev.dots === current.dots;
    default:
      return false;
  }
};

// Triangle SVG component - equilateral triangle
// Uses CSS transform for rotation to ensure perfect centering around bounding box center
const Triangle = ({ orientation, color, dots, size = 200 }) => {
  const colorValue = COLORS[color];
  
  // Get rotation angle based on orientation
  const getRotation = () => {
    switch (orientation) {
      case 'up': return 0;
      case 'right': return 90;
      case 'down': return 180;
      case 'left': return 270;
      default: return 0;
    }
  };
  
  // Calculate equilateral triangle points - always pointing UP
  // Triangle is centered in bounding box (not by centroid) so rotation keeps visual center fixed
  const getPoints = () => {
    const margin = 15;
    const side = size - margin * 2;
    const height = side * Math.sqrt(3) / 2;
    const centerX = size / 2;
    const centerY = size / 2;
    const halfSide = side / 2;
    
    // Center the triangle's BOUNDING BOX in the SVG, not the centroid
    // Bounding box for up-pointing triangle: width = side, height = height
    // Top of triangle at centerY - height/2, bottom at centerY + height/2
    const top = centerY - height / 2;
    const bottom = centerY + height / 2;
    
    // Vertices: tip at top center, base corners at bottom
    return `${centerX},${top} ${centerX - halfSide},${bottom} ${centerX + halfSide},${bottom}`;
  };

  // Calculate dot positions based on orientation and count
  // Each dot should have equal distance from all edges (along angle bisector from vertex)
  const getDotPositions = () => {
    const center = size / 2;
    const margin = 15;
    const side = size - margin * 2;
    const height = side * Math.sqrt(3) / 2;
    const halfSide = side / 2;
    
    // Triangle bounding box centered in SVG
    const top = center - height / 2;
    const bottom = center + height / 2;
    
    // Dots should be placed along angle bisectors at a fixed distance from vertex
    const inset = size * 0.18;
    
    // Actual vertex positions for UP triangle (bounding box centered)
    const tipVertex = { x: center, y: top };
    const leftVertex = { x: center - halfSide, y: bottom };
    const rightVertex = { x: center + halfSide, y: bottom };
    
    // Centroid is at 1/3 from base (2/3 from tip)
    const centroid = { x: center, y: top + height * 2 / 3 };
    
    const moveTowardCentroid = (vertex, dist) => {
      const dx = centroid.x - vertex.x;
      const dy = centroid.y - vertex.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      return [vertex.x + (dx / len) * dist, vertex.y + (dy / len) * dist];
    };
    
    const tipDot = moveTowardCentroid(tipVertex, inset);
    const leftDot = moveTowardCentroid(leftVertex, inset);
    const rightDot = moveTowardCentroid(rightVertex, inset);
    
    // Base center dot - aligned with the other base dots (same Y as leftDot/rightDot)
    const baseCenterDot = [center, leftDot[1]];
    
    let positions = [];
    switch (dots) {
      case 1:
        // Single dot near the TIP
        positions = [tipDot];
        break;
      case 2:
        // Two dots at BASE corners
        positions = [leftDot, rightDot];
        break;
      case 3:
        // Three dots at all 3 corners
        positions = [tipDot, leftDot, rightDot];
        break;
      case 4:
        // Four dots: 3 corners + center of base
        positions = [tipDot, leftDot, rightDot, baseCenterDot];
        break;
      default:
        positions = [];
    }

    // No rotation needed here - CSS transform handles it
    return positions;
  };

  return (
    <svg 
      width={size} 
      height={size} 
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: `rotate(${getRotation()}deg)` }}
    >
      <polygon
        points={getPoints()}
        fill={colorValue}
        stroke={BORDER_COLOR}
        strokeWidth="0.5"
      />
      {getDotPositions().map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={4.7} fill="#000" />
      ))}
    </svg>
  );
};

// Button component
const GameButton = ({ position, onClick, feedback, disabled, hidden }) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayColor, setOverlayColor] = useState(null);
  const [isFading, setIsFading] = useState(false);
  const feedbackRef = useRef(null);

  useEffect(() => {
    // Clear any existing timers when feedback changes
    if (feedbackRef.current) {
      clearTimeout(feedbackRef.current.fadeTimer);
      clearTimeout(feedbackRef.current.hideTimer);
      feedbackRef.current = null;
    }
    
    if (feedback !== null) {
      const color = feedback ? '#00ff01' : '#fe0000';
      setOverlayColor(color);
      setShowOverlay(true);
      setIsFading(false);
      
      // Start fading after 150ms
      const fadeTimer = setTimeout(() => {
        setIsFading(true);
      }, 150);
      
      // Hide completely after fade (150ms + 350ms fade = 500ms total)
      const hideTimer = setTimeout(() => {
        setShowOverlay(false);
        setOverlayColor(null);
        setIsFading(false);
      }, 500);
      
      feedbackRef.current = { fadeTimer, hideTimer };
      
      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    } else {
      // When feedback becomes null, immediately reset overlay state
      setShowOverlay(false);
      setOverlayColor(null);
      setIsFading(false);
    }
  }, [feedback]);

  if (hidden) return null;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-36 h-36 rounded relative overflow-hidden"
      style={{ 
        background: 'linear-gradient(180deg, #ecebeb 0%, #c8c8c8 100%)',
        border: `2px solid ${BORDER_COLOR}`,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {/* Feedback overlay with fade animation */}
      {showOverlay && overlayColor && (
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{ 
            backgroundColor: overlayColor,
            opacity: isFading ? 0 : 1,
            transition: 'opacity 350ms ease-out'
          }}
        />
      )}
    </button>
  );
};

const SKTGame = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  // The board (triangle plus its three buttons) is about 560px square
  const boardScale = useFitScale(560, 560);
  const gamesBase = useGamesBase();
  const location = useLocation();
  
  const { difficultyLevel: rawDifficultyLevel = 1, runTime = 45, rounds = 10, feedback = true, subject } = location.state || {};
  const difficultyLevel = parseInt(rawDifficultyLevel) || 1; // Ensure it's a number

  // Game states
  const [gamePhase, setGamePhase] = useState('loading'); // loading, attention, rules, playing, roundEnd, results
  const [currentRound, setCurrentRound] = useState(0);
  const [rules, setRules] = useState({ topRule: '', bottomRule: '' });
  const [rulesTimer, setRulesTimer] = useState(15);
  const [roundTimer, setRoundTimer] = useState(runTime);
  const [currentTriangle, setCurrentTriangle] = useState(null);
  const [previousTriangle, setPreviousTriangle] = useState(null);
  const [isFirstItem, setIsFirstItem] = useState(true);
  const [buttonFeedback, setButtonFeedback] = useState({ top: null, right: null, bottom: null });
  
  // Results tracking
  const [roundsData, setRoundsData] = useState([]);
  const [currentRoundData, setCurrentRoundData] = useState({ items_processed: 0, correct: 0, incorrect: 0 });
  const [finalResults, setFinalResults] = useState(null); // Store final results when game ends
  const [overallStats, setOverallStats] = useState(null); // Historical stats from backend
  
  // Per-rule accuracy tracking
  const [ruleStats, setRuleStats] = useState({
    ORIENTATION: { correct: 0, total: 0 },
    COLOUR: { correct: 0, total: 0 },
    'NUMBER OF POINTS': { correct: 0, total: 0 }
  });
  
  // Exit confirmation modal
  const [showExitModal, setShowExitModal] = useState(false);
  
  // Refs for timers and data
  const rulesTimerRef = useRef(null);
  const roundTimerRef = useRef(null);
  const startTimeRef = useRef(null);
  const allRoundsRef = useRef([]); // Sync ref for all rounds data
  const currentRoundRef = useRef({ items_processed: 0, correct: 0, incorrect: 0 }); // Sync ref for current round

  // Initialize game - go straight to attention phase
  useEffect(() => {
    const timer = setTimeout(() => {
      setGamePhase('attention');
    }, 500);

    return () => {
      clearTimeout(timer);
      document.body.style.backgroundColor = '';
      document.body.style.transition = '';
      if (rulesTimerRef.current) clearInterval(rulesTimerRef.current);
      if (roundTimerRef.current) clearInterval(roundTimerRef.current);
    };
  }, []);

  // Apply white background when game starts (attention phase onwards)
  useEffect(() => {
    if (gamePhase === 'attention' || gamePhase === 'rules' || gamePhase === 'playing' || gamePhase === 'results') {
      document.body.style.transition = 'background-color 0.5s ease';
      document.body.style.backgroundColor = '#ffffff';
    }
  }, [gamePhase]);

  // Handle attention phase
  useEffect(() => {
    if (gamePhase === 'attention') {
      const timer = setTimeout(() => {
        startNewRound();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [gamePhase]);

  // Rules countdown timer
  useEffect(() => {
    if (gamePhase === 'rules') {
      setRulesTimer(15);
      rulesTimerRef.current = setInterval(() => {
        setRulesTimer(prev => {
          if (prev <= 1) {
            clearInterval(rulesTimerRef.current);
            startPlaying();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(rulesTimerRef.current);
    }
  }, [gamePhase]);

  // Round timer
  useEffect(() => {
    if (gamePhase === 'playing') {
      setRoundTimer(runTime);
      roundTimerRef.current = setInterval(() => {
        setRoundTimer(prev => {
          if (prev <= 1) {
            clearInterval(roundTimerRef.current);
            endRound();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(roundTimerRef.current);
    }
  }, [gamePhase, currentRound]);

  const fetchOverallStats = async () => {
    try {
      const data = await apiClient.get(`/api/courses/skt-game/stats/${subjectId}/`);
      setOverallStats(data);
    } catch (error) {
      console.error('Failed to fetch overall stats:', error);
    }
  };

  const startNewRound = () => {
    const newRules = getRandomRules();
    setRules(newRules);
    setGamePhase('rules');
    // Reset both state and ref
    const emptyRound = { items_processed: 0, correct: 0, incorrect: 0 };
    setCurrentRoundData(emptyRound);
    currentRoundRef.current = emptyRound;
  };

  const startPlaying = () => {
    setIsFirstItem(true);
    setPreviousTriangle(null);
    setCurrentTriangle(generateValidTriangle(null));
    setGamePhase('playing');
    startTimeRef.current = Date.now();
  };

  const endRound = () => {
    // Save round data from ref (synchronous, always up-to-date)
    const roundDataToSave = { ...currentRoundRef.current };
    
    // Update ref synchronously for immediate access
    allRoundsRef.current = [...allRoundsRef.current, roundDataToSave];
    
    // Also update state for React re-renders
    setRoundsData(allRoundsRef.current);
    
    const nextRound = currentRound + 1;
    
    if (nextRound >= rounds) {
      // All rounds complete - calculate and store final results
      const allData = allRoundsRef.current;
      const totalItems = allData.reduce((sum, r) => sum + (r.items_processed || 0), 0);
      const totalCorrect = allData.reduce((sum, r) => sum + (r.correct || 0), 0);
      setFinalResults({
        totalItems,
        totalCorrect,
        avgItemsPerRound: allData.length > 0 ? (totalItems / allData.length).toFixed(1) : '0.0',
        accuracy: totalItems > 0 ? ((totalCorrect / totalItems) * 100).toFixed(1) : '0.0',
        roundsCount: allData.length
      });
      // Fetch overall stats from backend
      fetchOverallStats();
      setGamePhase('results');
    } else {
      setCurrentRound(nextRound);
      setGamePhase('attention');
    }
  };

  const handleButtonClick = useCallback((button) => {
    if (gamePhase !== 'playing') return;

    if (isFirstItem) {
      // First item - only skip is valid
      if (button === 'right') {
        setPreviousTriangle(currentTriangle);
        setCurrentTriangle(generateValidTriangle(currentTriangle));
        setIsFirstItem(false);
        // Update both ref and state
        currentRoundRef.current = { ...currentRoundRef.current, items_processed: currentRoundRef.current.items_processed + 1 };
        setCurrentRoundData(currentRoundRef.current);
      }
      return;
    }

    // Determine correct answer
    const topMatch = checkRuleMatch(previousTriangle, currentTriangle, rules.topRule);
    const bottomMatch = checkRuleMatch(previousTriangle, currentTriangle, rules.bottomRule);
    
    let correctButton;
    let activeRule = null; // Track which rule applies
    if (topMatch) {
      correctButton = 'top';
      activeRule = rules.topRule;
    } else if (bottomMatch) {
      correctButton = 'bottom';
      activeRule = rules.bottomRule;
    } else {
      correctButton = 'right';
      // No rule matches - this is a "skip" scenario
    }

    const isCorrect = button === correctButton;

    // Show feedback if enabled
    if (feedback) {
      setButtonFeedback({ top: null, right: null, bottom: null, [button]: isCorrect });
      setTimeout(() => setButtonFeedback({ top: null, right: null, bottom: null }), 150);
    }

    // Update stats - both ref (sync) and state
    const newRoundData = {
      items_processed: currentRoundRef.current.items_processed + 1,
      correct: currentRoundRef.current.correct + (isCorrect ? 1 : 0),
      incorrect: currentRoundRef.current.incorrect + (isCorrect ? 0 : 1),
    };
    currentRoundRef.current = newRoundData;
    setCurrentRoundData(newRoundData);
    
    // Update per-rule stats
    if (activeRule) {
      setRuleStats(prev => ({
        ...prev,
        [activeRule]: {
          correct: prev[activeRule].correct + (isCorrect ? 1 : 0),
          total: prev[activeRule].total + 1
        }
      }));
    }

    // Move to next triangle
    setPreviousTriangle(currentTriangle);
    setCurrentTriangle(generateValidTriangle(currentTriangle));
  }, [gamePhase, isFirstItem, previousTriangle, currentTriangle, rules, feedback]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gamePhase !== 'playing') return;
      
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          handleButtonClick('top');
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          handleButtonClick('right');
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          handleButtonClick('bottom');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleButtonClick, gamePhase]);

  const handleExit = () => {
    if (gamePhase === 'playing' || gamePhase === 'rules') {
      setShowExitModal(true);
    } else {
      navigate(`${gamesBase}/skt/${subjectId}`);
    }
  };
  
  const confirmExit = () => {
    setShowExitModal(false);
    navigate(`${gamesBase}/skt/${subjectId}`);
  };
  
  const cancelExit = () => {
    setShowExitModal(false);
  };

  const handleSaveResults = async () => {
    // Calculate per-rule accuracy percentages
    const ruleAccuracy = {
      orientation: ruleStats.ORIENTATION.total > 0 
        ? (ruleStats.ORIENTATION.correct / ruleStats.ORIENTATION.total * 100).toFixed(1) 
        : null,
      colour: ruleStats.COLOUR.total > 0 
        ? (ruleStats.COLOUR.correct / ruleStats.COLOUR.total * 100).toFixed(1) 
        : null,
      dots: ruleStats['NUMBER OF POINTS'].total > 0 
        ? (ruleStats['NUMBER OF POINTS'].correct / ruleStats['NUMBER OF POINTS'].total * 100).toFixed(1) 
        : null
    };
    
    // Use the ref which has all rounds data synchronously
    const allRounds = allRoundsRef.current.length > 0 ? allRoundsRef.current : roundsData;
    
    try {
      await apiClient.post('/api/courses/skt-game/runs/', {
        subject: subjectId,
        difficulty_level: difficultyLevel,
        rounds_data: allRounds,
        rule_stats: ruleStats,
        rule_accuracy: ruleAccuracy,
        started_at: new Date(startTimeRef.current).toISOString(),
      });
      navigate(`${gamesBase}/skt/${subjectId}`);
    } catch (error) {
      console.error('Failed to save results:', error);
      navigate(`${gamesBase}/skt/${subjectId}`);
    }
  };

  // Use finalResults state for display (set when game ends)
  const avgItemsPerRound = finalResults?.avgItemsPerRound || '0.0';
  const accuracy = finalResults?.accuracy || '0.0';

  return (
    <div className="fixed inset-0 top-16 bg-white flex flex-col overflow-hidden" style={{ animation: 'fadeFromBlack 1.5s ease-out' }}>
      <style>{`
        @keyframes fadeFromBlack {
          0% { filter: brightness(0); }
          100% { filter: brightness(1); }
        }
      `}</style>
      {/* Exit button */}
      <button
        onClick={handleExit}
        className="absolute top-4 right-4 p-2 bg-gray-800 hover:bg-gray-700 rounded-lg z-50"
      >
        <XMarkIcon className="w-6 h-6 text-white" />
      </button>

      {/* Loading Phase */}
      {gamePhase === 'loading' && (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      )}

      {/* Attention Phase */}
      {gamePhase === 'attention' && (
        <div className="flex-1 flex items-center justify-center">
          <h1 className="text-2xl font-bold text-red-500 tracking-wide">
            ATTENTION, MEMORIZE RULES!
          </h1>
        </div>
      )}

      {/* Rules Phase */}
      {gamePhase === 'rules' && (
        <div className="flex-1 flex flex-col items-center justify-between px-6 pb-4 pt-16 sm:px-8 sm:py-4">
          {/* Top rule - aligned to top */}
          <div className="text-center">
            <p className="text-lg text-gray-800 sm:text-2xl">
              1. If two triangles with the same <span className="font-bold">{rules.topRule}</span> follow in sequence,
              <br />then mark the button <span className="font-bold">ABOVE</span> the triangle!
            </p>
          </div>
          
          {/* Bottom rule - aligned to bottom */}
          <div className="text-center">
            <p className="text-lg text-gray-800 sm:text-2xl">
              2. If two triangles with the same <span className="font-bold">{rules.bottomRule}</span> follow in sequence,
              <br />then mark the button <span className="font-bold">BELOW</span> the triangle!
            </p>
          </div>
        </div>
      )}

      {/* Playing Phase */}
      {gamePhase === 'playing' && (
        <div className="flex-1 flex flex-col items-center justify-center relative" style={{ marginTop: -100 * boardScale }}>
          {/* Game area (scaled down as a whole on small screens) */}
          <div className="relative flex items-center justify-center" style={{ transform: `scale(${boardScale})` }}>
            {/* Top button - hidden on first item, centered horizontally */}
            <div className="absolute -top-44 left-1/2 -translate-x-1/2">
              <GameButton
                position="top"
                onClick={() => handleButtonClick('top')}
                feedback={buttonFeedback.top}
                disabled={false}
                hidden={isFirstItem}
              />
            </div>

            {/* Triangle */}
            {currentTriangle && (
              <Triangle
                orientation={currentTriangle.orientation}
                color={currentTriangle.color}
                dots={currentTriangle.dots}
                size={200}
              />
            )}

            {/* Right button - always visible, centered vertically */}
            <div className="absolute -right-44 top-1/2 -translate-y-1/2">
              <GameButton
                position="right"
                onClick={() => handleButtonClick('right')}
                feedback={buttonFeedback.right}
                disabled={false}
                hidden={false}
              />
            </div>

            {/* Bottom button - hidden on first item, centered horizontally */}
            <div className="absolute -bottom-44 left-1/2 -translate-x-1/2">
              <GameButton
                position="bottom"
                onClick={() => handleButtonClick('bottom')}
                feedback={buttonFeedback.bottom}
                disabled={false}
                hidden={isFirstItem}
              />
            </div>
          </div>
        </div>
      )}

      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Exit Training?</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to exit? Your progress will be lost.</p>
            <div className="flex gap-3">
              <button
                onClick={cancelExit}
                className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmExit}
                className="flex-1 py-2.5 px-4 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Results Phase */}
      {gamePhase === 'results' && (() => {
        // Helper function to get color on red-to-green spectrum
        const getPercentageColor = (percentage) => {
          const value = Math.max(0, Math.min(100, percentage));
          if (value <= 50) {
            const ratio = value / 50;
            const r = 239;
            const g = Math.round(68 + (180 * ratio));
            const b = 68;
            return `rgb(${r}, ${g}, ${b})`;
          } else {
            const ratio = (value - 50) / 50;
            const r = Math.round(239 - (205 * ratio));
            const g = Math.round(248 - (70 * ratio));
            const b = Math.round(68 - (34 * ratio));
            return `rgb(${r}, ${g}, ${b})`;
          }
        };
        
        const itemsNum = parseFloat(avgItemsPerRound) || 0;
        const accuracyNum = parseFloat(accuracy) || 0;
        const totalScore = (itemsNum + accuracyNum) / 2;
        
        const orientationPct = ruleStats.ORIENTATION.total > 0 
          ? (ruleStats.ORIENTATION.correct / ruleStats.ORIENTATION.total * 100) : null;
        const colourPct = ruleStats.COLOUR.total > 0 
          ? (ruleStats.COLOUR.correct / ruleStats.COLOUR.total * 100) : null;
        const dotsPct = ruleStats['NUMBER OF POINTS'].total > 0 
          ? (ruleStats['NUMBER OF POINTS'].correct / ruleStats['NUMBER OF POINTS'].total * 100) : null;
        
        return (
        <div className="flex-1 flex flex-col items-center overflow-y-auto py-6 px-4 md:px-8">
          <div className="text-center space-y-4 md:space-y-6 max-w-md w-full">
            {/* Total Score at Top */}
            <div>
              <h2 className="text-xl text-gray-700">Total Score</h2>
              <p className="text-6xl font-bold" style={{ color: getPercentageColor(totalScore) }}>{totalScore.toFixed(1)}%</p>
            </div>
            
            {/* Accuracy and Items/Run side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm text-gray-600 mb-1">Accuracy</h3>
                <p className="text-3xl font-bold" style={{ color: getPercentageColor(accuracyNum) }}>{accuracy}%</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm text-gray-600 mb-1">Items/Round</h3>
                <p className="text-3xl font-bold" style={{ color: getPercentageColor(Math.min(itemsNum, 100)) }}>{avgItemsPerRound}</p>
              </div>
            </div>

            {/* Rule Accuracy Section */}
            <div className="bg-gray-50 rounded-lg p-4 mt-4">
              <p className="text-sm text-gray-600 mb-3">Accuracy by Rule</p>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-xs text-gray-500">Orientation</p>
                  <p className="text-lg font-bold" style={{ color: orientationPct !== null ? getPercentageColor(orientationPct) : '#9CA3AF' }}>
                    {orientationPct !== null ? `${orientationPct.toFixed(0)}%` : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Colour</p>
                  <p className="text-lg font-bold" style={{ color: colourPct !== null ? getPercentageColor(colourPct) : '#9CA3AF' }}>
                    {colourPct !== null ? `${colourPct.toFixed(0)}%` : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Number of Dots</p>
                  <p className="text-lg font-bold" style={{ color: dotsPct !== null ? getPercentageColor(dotsPct) : '#9CA3AF' }}>
                    {dotsPct !== null ? `${dotsPct.toFixed(0)}%` : '-'}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 space-y-3">
              <h3 className="text-lg font-medium text-gray-800">Overall training results</h3>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">Number of training runs</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-500">Level 1</p>
                    <p className="text-lg font-bold text-gray-900">
                      {(overallStats?.level_stats?.[1]?.total_runs || 0) + (difficultyLevel === 1 ? 1 : 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Level 2</p>
                    <p className="text-lg font-bold text-gray-900">
                      {(overallStats?.level_stats?.[2]?.total_runs || 0) + (difficultyLevel === 2 ? 1 : 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Level 3</p>
                    <p className="text-lg font-bold text-gray-900">
                      {(overallStats?.level_stats?.[3]?.total_runs || 0) + (difficultyLevel === 3 ? 1 : 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">Personal highscore</p>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-500">Level 1</p>
                    <p className="text-sm text-gray-900">Items: <span className="font-bold text-blue-600">
                      {difficultyLevel === 1 
                        ? Math.max(parseFloat(avgItemsPerRound) || 0, overallStats?.level_stats?.[1]?.best_items_per_round || 0).toFixed(1)
                        : (overallStats?.level_stats?.[1]?.best_items_per_round || '-')}
                    </span></p>
                    <p className="text-sm text-gray-900">Accuracy: <span className="font-bold text-green-600">
                      {difficultyLevel === 1 
                        ? `${Math.max(parseFloat(accuracy) || 0, overallStats?.level_stats?.[1]?.best_accuracy || 0).toFixed(1)}%`
                        : (overallStats?.level_stats?.[1]?.best_accuracy ? `${overallStats.level_stats[1].best_accuracy}%` : '-')}
                    </span></p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Level 2</p>
                    <p className="text-sm text-gray-900">Items: <span className="font-bold text-blue-600">
                      {difficultyLevel === 2 
                        ? Math.max(parseFloat(avgItemsPerRound) || 0, overallStats?.level_stats?.[2]?.best_items_per_round || 0).toFixed(1)
                        : (overallStats?.level_stats?.[2]?.best_items_per_round || '-')}
                    </span></p>
                    <p className="text-sm text-gray-900">Accuracy: <span className="font-bold text-green-600">
                      {difficultyLevel === 2 
                        ? `${Math.max(parseFloat(accuracy) || 0, overallStats?.level_stats?.[2]?.best_accuracy || 0).toFixed(1)}%`
                        : (overallStats?.level_stats?.[2]?.best_accuracy ? `${overallStats.level_stats[2].best_accuracy}%` : '-')}
                    </span></p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Level 3</p>
                    <p className="text-sm text-gray-900">Items: <span className="font-bold text-blue-600">
                      {difficultyLevel === 3 
                        ? Math.max(parseFloat(avgItemsPerRound) || 0, overallStats?.level_stats?.[3]?.best_items_per_round || 0).toFixed(1)
                        : (overallStats?.level_stats?.[3]?.best_items_per_round || '-')}
                    </span></p>
                    <p className="text-sm text-gray-900">Accuracy: <span className="font-bold text-green-600">
                      {difficultyLevel === 3 
                        ? `${Math.max(parseFloat(accuracy) || 0, overallStats?.level_stats?.[3]?.best_accuracy || 0).toFixed(1)}%`
                        : (overallStats?.level_stats?.[3]?.best_accuracy ? `${overallStats.level_stats[3].best_accuracy}%` : '-')}
                    </span></p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4 pb-4">
              <button
                onClick={() => {
                  setRoundsData([]);
                  allRoundsRef.current = []; // Reset the ref too
                  currentRoundRef.current = { items_processed: 0, correct: 0, incorrect: 0 }; // Reset current round ref
                  setFinalResults(null); // Reset final results
                  setCurrentRound(0);
                  setRuleStats({
                    ORIENTATION: { correct: 0, total: 0 },
                    COLOUR: { correct: 0, total: 0 },
                    'NUMBER OF POINTS': { correct: 0, total: 0 }
                  });
                  setGamePhase('attention');
                }}
                className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
              >
                Continue Training
              </button>
              <button
                onClick={handleSaveResults}
                className="flex-1 py-3 bg-gray-700 hover:bg-gray-800 text-white rounded-lg font-medium transition-colors"
              >
                Exit Training
              </button>
            </div>
          </div>
        </div>
        );
      })()}

    </div>
  );
};

export default SKTGame;
